import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AuthService,
  ButtonsComponent,
  CartService,
  CartsService,
  CartTotalsService,
  CheckoutConfigService,
  Customer,
  GungModalService,
  NavbarCartItem,
  NavbarCartModel,
  NavbarCartService,
  SaveCartModalConfigService,
  SelectedCustomerService
} from 'gung-standard';
import { combineLatest, first, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import {
  CheckoutSettingsGroup,
  HlDisplayPortalFeaturesService,
  PriceSettingsGroup,
  ProductListInfoSettingsGroup
} from '../../../services/hl-display-portal-features.service';
import { HlDisplayAdditionalCostsService } from '../../../services/hl-display-additional-costs.service';
import { HlDisplayFreightService } from '../../../services/hl-display-freight.service';
import { HLDisplayCheckoutUtilService } from '../../../services/hldisplay-checkout-util.service';

@Component({
  selector: 'app-hl-checkout-buttons-portal',
  templateUrl: './hl-checkout-buttons-portal.component.html',
  styleUrl: './hl-checkout-buttons-portal.component.css',
  standalone: false
})
export class HlCheckoutButtonsPortalComponent extends ButtonsComponent implements OnInit {
  public showApprovalQuote: boolean;
  public showCreateOrder: boolean;
  public showCreateSampleOrder: boolean;
  public showSavedQuotes: boolean;
  isApprovalQuote: boolean;
  isQuote: boolean;
  isSampleOrder: boolean;
  loading: boolean = true;
  isManager: boolean = false;
  isCustomerService: boolean = false;

  public minOrderValue: number;
  freightCostMin: number = 0;
  brokenBoxFee: number = 0;

  skipAdminCharge: boolean = false;
  skipBrokenBoxFee: boolean = false;

  freightCost: number = 0;
  adminCharge: number = 0;
  adminChargeMin: number = 0;
  public brokenBoxFeeBaseCost: number = 0;
  public orderTotalValue: number = 0;

  blockFullAutomationByCSO: boolean = false;

  additionalCosts: any;
  public totalDiscount: number;

  @Output() totalDiscountOutput: EventEmitter<number> = new EventEmitter<number>();

  hasMultiplePOD: boolean = false;

  customerKusEditText: string = '';
  displayZeroAdminFee: boolean = false;

  totalWeightInCart: number = 0;

  protected hidePrices: boolean = false;
  isTemplatePortal: boolean = false;

  constructor(
    protected navbarCartService: NavbarCartService,
    protected gungModalService: GungModalService,
    protected saveCartModalConfigService: SaveCartModalConfigService,
    protected cartService: CartService,
    protected translationService: TranslateService,
    protected authService: AuthService,
    protected checkoutConfig: CheckoutConfigService,
    protected hlDisplayPortalFeaturesService: HlDisplayPortalFeaturesService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected hlDisplayAdditionalCostsService: HlDisplayAdditionalCostsService,
    protected selectedCustomerService: SelectedCustomerService,
    private cartTotalsService: CartTotalsService,
    protected hlDisplayFreightService: HlDisplayFreightService,
    protected cartsService: CartsService,
    private hlDisplayCheckoutUtilService: HLDisplayCheckoutUtilService
  ) {
    super(
      navbarCartService,
      gungModalService,
      saveCartModalConfigService,
      cartService,
      translationService,
      authService,
      checkoutConfig,
      cartsService
    );
  }

  ngOnInit() {
    this.updateData();

    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales = roles.indexOf('SALES') > -1;
        this.isAdmin = roles.indexOf('ADMIN') > -1;
        this.isUser = roles.indexOf('USER') > -1 && !this.isSales && !this.isAdmin;
        this.isManager = roles.indexOf('MANAGER') > -1;
        this.isCustomerService = roles.indexOf('CUSTOMER_SERVICE') > -1;
        this.rolesLoaded();
      });

    forkJoin({
      checkoutSettingsGroup: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('orderCheckoutSettingsGroup')
        .pipe(
          first(),
          map(checkoutSettingsGroup => checkoutSettingsGroup as CheckoutSettingsGroup)
        ),
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      productListInfoSettingsGroup: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('productListInfoSettingsGroup')
        .pipe(
          first(),
          map(productListInfoSettingsGroup => productListInfoSettingsGroup as ProductListInfoSettingsGroup)
        )
    })
      .pipe(
        first(),
        switchMap(({ checkoutSettingsGroup, selectedCustomer, productListInfoSettingsGroup }) => {
          return combineLatest({
            checkoutSettingsGroup: of(checkoutSettingsGroup).pipe(first()),
            additionalCostTerms: checkoutSettingsGroup.useTradePolicy
              ? this.hlDisplayAdditionalCostsService.getAdditionalCostTerms(selectedCustomer).pipe(first())
              : of(null),
            customFreight: this.hlDisplayFreightService.getCustomFreight(selectedCustomer).pipe(first()),
            totals: this.cartTotalsService.getTotals(),
            priceSettings: this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('priceSettingsGroup').pipe(
              first(),
              map(priceSettingsGroup => priceSettingsGroup as PriceSettingsGroup)
            ),
            contents: this.navbarCartService.getContents(),
            skipAdminFee: this.hlDisplayCheckoutUtilService
              .getSkipAdminFeeObservable()
              .pipe(takeUntil(this.unsubscribe)),
            skipBrokenBoxFee: this.hlDisplayCheckoutUtilService
              .getSkipBrokenBoxFeeObservable()
              .pipe(takeUntil(this.unsubscribe)),
            productListInfoSettingsGroup: of(productListInfoSettingsGroup).pipe(first())
          }).pipe(takeUntil(this.unsubscribe));
        })
      )
      .subscribe(
        ({
          checkoutSettingsGroup,
          additionalCostTerms,
          customFreight,
          totals,
          priceSettings,
          contents,
          skipAdminFee,
          skipBrokenBoxFee,
          productListInfoSettingsGroup
        }) => {
          if (contents.currency !== '') {
            this.cartCurrency = contents.currency;
          } else {
            this.cartCurrency = priceSettings.defaultCurrencyCode;
          }
          this.isTemplatePortal = productListInfoSettingsGroup.templatePortal;
          this.hidePrices = priceSettings?.hidePrices;

          this.cartContents = contents;
          this.cartTotalRows = contents.rows.length;
          this.cartTotalItems = contents.rows.map(row => row.cartRow.qty).reduce((acc, curr) => acc + curr, 0);

          this.minOrderValue = checkoutSettingsGroup.minimumOrderValue;
          this.displayZeroAdminFee = checkoutSettingsGroup.displayZeroAdminFee;

          // Copy the order total value to cart total value without reference
          this.cartTotalValue = JSON.parse(JSON.stringify(totals.totalValue));
          this.orderTotalValue = totals.totalValue;

          if (additionalCostTerms != null) {
            const freightCostItem = this.hlDisplayAdditionalCostsService.getFreightCharge(
              this.orderTotalValue,
              additionalCostTerms
            );

            if (freightCostItem) {
              this.freightCost = freightCostItem.value;
              this.freightCostMin = freightCostItem.freightMin;
            }

            // Set in the HLDisplayCheckoutUtilService to be used in other components
            this.skipAdminCharge = skipAdminFee;
            this.skipBrokenBoxFee = skipBrokenBoxFee;
            const adminFeeItem = this.hlDisplayAdditionalCostsService.getAdminFee(
              this.orderTotalValue,
              additionalCostTerms
            );

            if (adminFeeItem && !this.skipAdminCharge) {
              this.adminCharge = adminFeeItem.value;
              this.adminChargeMin = adminFeeItem.adminChargeMin;
            }

            const minOrderItem = this.hlDisplayAdditionalCostsService.getMinOrder(additionalCostTerms);

            if (minOrderItem) {
              this.minOrderValue = minOrderItem.value;
            }

            if (additionalCostTerms.brokenBoxFee > 0) {
              this.brokenBoxFeeBaseCost = additionalCostTerms.brokenBoxFee;
            }

            this.cartTotalValue = totals.totalValue;
          } else if (!!customFreight && customFreight.freightLevels.length > 0) {
            const sortedCustomFreight = customFreight.freightLevels.sort(
              (a, b) => b.upperLevelLimit - a.upperLevelLimit
            );
            let customFreightCost = 0;
            let customFreightMin = 0;

            for (let i = 0; i < sortedCustomFreight.length; i++) {
              if (this.orderTotalValue < sortedCustomFreight[i].upperLevelLimit) {
                customFreightCost = sortedCustomFreight[i].freightCost;
                customFreightMin = sortedCustomFreight[i].upperLevelLimit;
              }
            }

            this.freightCost = customFreightCost;
            this.freightCostMin = customFreightMin;
          }

          if (this.cartTotalValue < this.adminChargeMin && !this.skipAdminCharge) {
            this.orderTotalValue += this.adminCharge;
          }

          // Order is important, to not allow for free freight cost because of other fees
          if (this.cartTotalValue < this.freightCostMin) {
            this.orderTotalValue += this.freightCost;
          }

          // Here to prevent timing issue
          if (this.brokenBoxFeeBaseCost > 0 && this.cartContents && !this.skipBrokenBoxFee) {
            this.brokenBoxFee = this.hlDisplayAdditionalCostsService.calculateBrokenBoxFee(
              this.cartContents.rows,
              this.brokenBoxFeeBaseCost
            );
          }

          if (!this.skipBrokenBoxFee) {
            this.orderTotalValue += this.brokenBoxFee;
          }

          this.applyDiscountToTotalValue(null, this._discountCode);

          this.checkForMultiplePOD(this.cartContents.rows);

          this.calculateTotalWeight(contents);

          this.changeDetectorRef.detectChanges();
          this.loading = false;
        }
      );

    if (!!this.disableNextButtonObservable) {
      this.disableNextButtonObservable
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(disable => (this.disableNextButton = disable));
    }

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(first())
      .subscribe(customer => this.showCustomerText(customer));
  }

  rolesLoaded() {
    this.hlDisplayPortalFeaturesService
      .getPortalFeatureGroup('orderCheckoutSettingsGroup')
      .pipe(first())
      .subscribe(orderCheckoutSettingsGroup => {
        let orderCheckoutGroup = orderCheckoutSettingsGroup as CheckoutSettingsGroup;
        this.showApprovalQuote = orderCheckoutGroup.showApprovalQuote && (this.isManager || this.isSales);
        this.showCreateOrder = !this.isSales || orderCheckoutGroup.showCreateOrder;
        this.showCreateSampleOrder = orderCheckoutGroup.showCreateSampleOrder && (this.isManager || this.isSales);
        this.showSavedQuotes = orderCheckoutGroup.showSavedQuotes && (this.isSales || this.isCustomerService);
      });
  }

  sendApprovalQuote() {
    this.isApprovalQuote = true;
    this.nextClicked.emit({
      isApprovalQuote: true,
      _blockFullAutomationByCSO: this.blockFullAutomationByCSO
    });
  }

  createQuote() {
    this.isQuote = true;
    this.nextClicked.emit({
      isQuote: true,
      _blockFullAutomationByCSO: this.blockFullAutomationByCSO
    });
  }

  saveQuote(): void {
    this.saveCartModalConfigService.openSaveCartModal();
  }

  get showSaveQuoteButton(): boolean {
    return this.isFirstStep && this.showSavedQuotes;
  }

  createSampleOrder() {
    this.isSampleOrder = true;
    this.nextClicked.emit({
      isSampleOrder: true,
      _blockFullAutomationByCSO: this.blockFullAutomationByCSO
    });
  }

  createOrder() {
    this.nextClicked.emit({
      _blockFullAutomationByCSO: this.blockFullAutomationByCSO
    });
  }

  public onTotalDiscountChanged(): void {
    this.totalDiscountOutput.emit(this.totalDiscount);
  }

  private checkForMultiplePOD(cartItems: NavbarCartItem[]): void {
    const podIds: string[] = [];
    for (const item of cartItems) {
      if (!!item?.cartRow?.extra?.orp?.ordlevplats1 && !podIds.includes(item?.cartRow?.extra?.orp?.ordlevplats1)) {
        podIds.push(item.cartRow.extra.orp.ordlevplats1);
      }
    }

    this.hasMultiplePOD = podIds.length > 1;
  }

  nextClickedButton() {
    if (this.isFirstStep && this.hasMultiplePOD) {
      const htmlDescription: string = `
        <p class="mb-0" style="font-size: 14px">${this.translationService.instant('MULTIPLE_POD_CHECKOUT_TEXT_1')}, ${this.translationService.instant('MULTIPLE_POD_CHECKOUT_TEXT_2').toLowerCase()}</p>`;
      this.gungModalService
        .openBasicModal(
          this.translationService.instant('MULTIPLE_POD_SELECTED_MODAL_1'),
          htmlDescription,
          {},
          false,
          this.translationService.instant('OK')
        )
        .then(
          res => {
            this.nextClicked.emit();
          },
          rej => {
            this.nextClicked.emit();
          }
        );
    } else {
      this.nextClicked.emit();
    }
  }

  showCustomerText(customer: Customer): void {
    if (!!customer?.extra?.kus?.edit) {
      this.customerKusEditText = customer.extra.kus.edit.replace(/\n{2,}/g, '\n');
    }
  }

  protected calculateTotalWeight(contents: NavbarCartModel): void {
    this.totalWeightInCart = 0;
    for (const row of contents.rows) {
      const qty: number = row.cartRow.qty;
      const netWeight: number = Number(row.product.extra?.ar?.artvikt) || 0;
      this.totalWeightInCart += qty * netWeight;
    }
  }
}
